/**
 * Download button for the Adesa Run List — exports the run as an Excel working
 * sheet (.xlsx). One format, so it's a plain button (no menu). Disabled until a
 * run has been built in this session (the page passes `runId` only after Build
 * Run, so before that the button stays greyed).
 */
import React, { useState } from "react"
import { Download } from "lucide-react"

import { exportAuctionRunXlsx } from "../../api/wholesaleAuction"
import { THEME } from "../../config/theme"

interface Props {
  runId: number | null
  runStartedAt: string | null
}

export default function DownloadButton({ runId, runStartedAt }: Props) {
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const disabled = downloading || runId === null

  const handleDownload = async () => {
    if (runId === null) return
    setDownloading(true)
    setError(null)
    try {
      const blob = await exportAuctionRunXlsx(runId)
      triggerBrowserDownload(blob, formatFilename(runStartedAt))
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to download")
    } finally {
      setDownloading(false)
    }
  }

  const buttonStyle: React.CSSProperties = {
    height: 34,
    width: 150,
    boxSizing: "border-box",
    justifyContent: "center",
    padding: `0 ${THEME.spacing.sm}`,
    fontSize: 14,
    fontWeight: 500,
    borderRadius: THEME.borderRadius.md,
    border: "none",
    backgroundColor: THEME.colors.navy,
    color: THEME.colors.white,
    opacity: disabled ? 0.5 : 1,
    cursor: disabled ? "not-allowed" : "pointer",
    userSelect: "none",
    transition: "opacity 0.15s",
    display: "inline-flex",
    alignItems: "center",
    gap: THEME.spacing.xs,
    fontFamily: THEME.fonts.primary,
    whiteSpace: "nowrap",
  }

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button type="button" onClick={handleDownload} disabled={disabled} style={buttonStyle}>
        <Download size={14} strokeWidth={2} />
        {downloading ? "Preparing…" : "Download"}
      </button>

      {error && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            fontSize: 12,
            color: THEME.colors.semantic.danger,
            whiteSpace: "nowrap",
          }}
        >
          {error}
        </div>
      )}
    </div>
  )
}

function formatFilename(runStartedAt: string | null): string {
  const pad = (n: number) => n.toString().padStart(2, "0")
  const date = runStartedAt ? new Date(runStartedAt) : new Date()
  const datePart = `${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${date.getFullYear()}`
  return `Dolan_Adesa_Run_List_${datePart}.xlsx`
}

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}
